// PurchaseHistoryListSection.tsx
// Feature: 008-purchase-history-react
//
// Displays the data table, pagination, and download link.

import React, { useContext } from 'react'
import { useDtmAnalytics } from 'hooks/useDtmAnalytics'
import { setCustomClick } from 'utils/analyticsUtils'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import { Paging } from 'components/Paging'
import { PurchaseHistoryContext } from 'contexts/PurchaseHistoryContext'
import { PurchaseHistoryInfoSection } from './PurchaseHistoryInfoSection'
import { PurchaseHistoryListSectionStyled } from './PurchaseHistoryListSection.styled'
import NoActivity from 'modules/BuyerActivity/NoActivity'
import CaretDownFill from 'images/icon/CaretDownFill'
import CaretRightFill from 'images/icon/CaretRightFill'
import { formatDateTime } from 'utils/dateUtils'
import { IPurchaseHistoryResult } from 'types/purchaseHistoryTypes'
import { Button, OverlayTrigger } from 'react-bootstrap'
import { ChargePopover } from './ChargesPopOver'
import { DataTable } from 'components/DataTableHistory'
import { Link } from 'components/Share/Link'
import { useGlobalStore } from 'store/useGlobalStore'
import { ArbitrationState } from 'types/arbitrationTypes'
import { StyledToolTip } from 'modules/DealerVehicleManagement/VehicleDetails.styled'

const ARBITRATION_EXPIRED_TOOLTIP =
  'The window for physical damage claims has closed. Non physical damage claims may be considered, please use the Contact Us form for review. Include VIN in your submission.'

interface IProps {
  onArbitrateClick?: (vehicle: IPurchaseHistoryResult, isUpdateAttachment: boolean) => void
}

export const PurchaseHistoryListSection: React.FC<IProps> = ({ onArbitrateClick }) => {
  const { results, totalRecords, isLoading, errorMessages, hasSearched, searchFilter, changePage, changePageSize } =
    useContext(PurchaseHistoryContext)
  const getLocalText = useGlobalStore((state) => state.getLocalText)
  const { userInteraction } = useDtmAnalytics()
  const showArbitrationColumn = results?.some((result) => result.ShowArbitrationColumn) ?? false
  const totalPages = Math.max(1, Math.ceil(totalRecords / searchFilter.PageSize))
  const currentPage = searchFilter.PageNumber

  const renderArbitrationCell = (value: IPurchaseHistoryResult) => {
    if (!value.ShowArbitrationColumn) return null

    if (value.ArbitrationState === ArbitrationState.None) {
      return null
    }

    if (value.ArbitrationState === ArbitrationState.New) {
      return (
        <button
          type="button"
          className="link-history document-arbitration-link"
          onClick={() => onArbitrateClick?.(value, false)}
        >
          Arbitrate Vehicle
        </button>
      )
    }

    if (value.ArbitrationState === ArbitrationState.Submitted) {
      const hasStatus = !!value.LatestArbitrationStatus?.trim()
      const statusLabel = hasStatus ? value.LatestArbitrationStatus.trim().toUpperCase() : 'ARBITRATION SUBMITTED'

      return (
        <div className="arbitration-cell">
          <span className={`arbitration-label ${hasStatus ? 'arbitration-status' : 'arbitration-submitted'}`}>
            {statusLabel}
          </span>
          {value.CanAddDocuments && (
            <button
              type="button"
              className="link-history document-arbitration-link"
              onClick={() => onArbitrateClick?.(value, true)}
            >
              Add Attachments
            </button>
          )}
        </div>
      )
    }

    if (value.ArbitrationState === ArbitrationState.Expired) {
      const expiredTooltip = getLocalText('ArbitrationPeriodExpired', ARBITRATION_EXPIRED_TOOLTIP)

      return (
        <div className="arbitration-cell">
          <span className="arbitration-label arbitration-expired">
            Arbitration Period Expired
            <OverlayTrigger
              placement="top"
              overlay={
                <StyledToolTip id={`arbitration-expired-${value.VehicleInstanceID}`} placement="top">
                  <span>{expiredTooltip}</span>
                </StyledToolTip>
              }
            >
              <span className="arbitration-expired-icon" aria-label="Arbitration expired info">
                <FontAwesomeIcon icon={faInfoCircle} />
              </span>
            </OverlayTrigger>
          </span>
        </div>
      )
    }

    return null
  }

  if (!hasSearched && !isLoading) {
    return null
  }

  return (
    <PurchaseHistoryListSectionStyled>
      {!isLoading && hasSearched && totalRecords === 0 ? (
        <NoActivity message={errorMessages || 'No Purchase Vehicle Found'} />
      ) : (
        <>
          <DataTable
            data={results}
            isFilterSearch={false}
            // filterColumnAcesscor="Title"
            isDataPaginated={true}
            columns={[
              {
                id: 'Title',
                Header: 'Description',
                accessor: (x: IPurchaseHistoryResult) => x,
                Cell: ({ value }) => (
                  <Link
                    className="link-history"
                    to={`vehicle/${value.VehicleInstanceID}?history=true`}
                    onClick={() => setCustomClick('purchase history: vdp', { usePageName: false })}
                  >
                    {value.Title}
                  </Link>
                )
              },
              {
                id: 'Vin',
                Header: 'VIN',
                accessor: (x: IPurchaseHistoryResult) => x,
                Cell: ({ value }) => {
                  return <>{value.Vin}</>
                }
              },
              {
                Header: 'Sale Price',
                accessor: 'Charges',
                Cell: ({ value }) => (
                  <> {value.find((x) => x.VehicleChargeTypeDescription === 'Sale Price')?.PriceString} </>
                )
              },
              {
                id: 'Buy Fee',
                Header: 'Buy Fee',
                accessor: 'Charges',
                Cell: ({ value }) => (
                  <> {value.find((x) => x.VehicleChargeTypeDescription === 'Buy Fee')?.PriceString} </>
                )
              },
              {
                id: 'Total Price',
                Header: 'Total Price',
                accessor: (x: IPurchaseHistoryResult) => x,
                Cell: ({ value }) => (
                  <div>
                    {value?.Charges?.filter(
                      (x) =>
                        x.VehicleChargeTypeDescription !== 'Buy Fee' && x.VehicleChargeTypeDescription !== 'Sale Price'
                    ).length > 0 ? (
                      <OverlayTrigger
                        placement="top-end"
                        trigger="click"
                        rootClose
                        overlay={(props) => <ChargePopover charges={value?.Charges} {...props} />}
                      >
                        <Button variant="link" className="charges-link">
                          {value.TotalPriceString}
                        </Button>
                      </OverlayTrigger>
                    ) : (
                      <Button variant="link" className="price-str" disabled>
                        {value.TotalPriceString}
                      </Button>
                    )}
                  </div>
                )
              },
              {
                Header: getLocalText('DATE', 'Purchase Date'),
                accessor: 'PurchaseDate',
                Cell: ({ value }) => <>{formatDateTime(value)}</>
              },
              {
                Header: getLocalText('PAYMENT_METHOD', 'Payment Method'),
                accessor: 'PaymentType'
              },
              {
                Header: 'Document',
                accessor: 'Documents',
                Cell: ({ value }) => {
                  return (
                    <div className="documents">
                      {value?.length > 0
                        ? value.map((doc) => (
                            <Link
                              to={doc.DocumentURL}
                              className="link-history"
                              onClick={() => userInteraction('purchase history: download purchase confirmation')}
                            >
                              {doc.display}
                            </Link>
                          ))
                        : 'NA'}
                    </div>
                  )
                }
              },
              ...(showArbitrationColumn
                ? [
                    {
                      Header: 'Arbitration',
                      id: 'arbitration',
                      accessor: (x: IPurchaseHistoryResult) => x,
                      Cell: ({ value }: { value: IPurchaseHistoryResult }) => renderArbitrationCell(value)
                    }
                  ]
                : []),
              {
                Header: () => null,
                id: 'expander',
                Cell: ({ row }) => (
                  <span
                    className="action"
                    {...row.getToggleRowExpandedProps()}
                    onClick={(e) => {
                      userInteraction('purchase history: select')
                      row.getToggleRowExpandedProps().onClick?.(e)
                    }}
                  >
                    {row.isExpanded ? <CaretDownFill /> : <CaretRightFill />}
                  </span>
                )
              }
            ]}
            renderRowSubComponent={(row) => (
              <PurchaseHistoryInfoSection VIN={row?.original?.Vin} isLoadApi={row.isExpanded} />
            )}
          />
          <Paging
            totalPages={totalPages}
            currentPage={currentPage}
            currentPageSize={searchFilter.PageSize}
            handleChangedPage={(pageNum) => changePage(pageNum - 1)}
            handlePageSizeChanged={(pageSize) => changePageSize(pageSize)}
          />
        </>
      )}
    </PurchaseHistoryListSectionStyled>
  )
}

export default PurchaseHistoryListSection
